// Ambulation Zone — SystemCrash
// A fake page "crash" that ambushes the visitor while they scroll the main
// board. Once they pass deep enough down the page, the screen GLITCHES, throws
// a corrupted AMBULATE/OS kernel panic ("press any key"), then REBOOTS with a
// little POST sequence. When the reboot finishes it unlocks THE ANNEX
// (calls onUnlock) and dismisses. The closing thank-you no longer takes over
// the screen — App now rests a SUBTLE note at the bottom of the page instead.
//
// On-brand: CRT/VHS death, rogue-hacker terminal panic dump, scanlines, RGB
// chromatic aberration, the ever-watching eye. The moment the panic fires it
// also plays an ominous COMPUTERIZED tone-shift via WebAudio (synthesized, no
// audio file): a detuning descending sub-bass drop plus a dissonant bit-crushed
// digital cluster/sweep — gated on the global sound toggle (props.soundEnabled).
// When the panic is dismissed and the machine REBOOTS into the Annex, a dense
// layered set of synthesized retro-computer boot sounds plays (power-up hum/fan
// whir rising, POST beeps, a memory-test sweep, disk-seek chatter, hardware-init
// blips, a BIOS chime, then an "annex online" confirmation tone) timed to the
// streaming POST lines — also gated on props.soundEnabled.
// One-shot per session (sessionStorage), and honors prefers-reduced-motion
// (skips the glitch, holds the eye/pupil steady, keeps it short).
//
// Self-contained: the contract with App is the onUnlock() callback (fired once
// when the reboot finishes), an optional `soundEnabled` flag (threaded from the
// global sound toggle), plus an optional `replayRef` — App can stash a replay()
// function on it to RE-TRIGGER the crash on demand (e.g. from the "descend"
// hatch) even after it's already been seen this session. It detects the
// automatic scroll trigger itself.

import * as React from "react";
import "./systemcrash.css";
import crashEye from "../../../images/crasheye.png";
import { rafThrottle, prefersReducedMotion } from "../_shared/sketchFrame";

const SEEN_KEY = "ambulate_crashed";

// Boot/POST lines streamed out one-by-one on reboot.
const POST_LINES = [
    "> rebooting ambulate kernel .........",
    "> POST: core memory ............. OK",
    "> POST: the eye ................. AWAKE",
    "> fsck: floor integrity ......... CRACKED",
    "> mounting /annex ............... ONLINE",
    "> the annex is now available.",
];

class SystemCrash extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            // idle -> glitch -> panic -> reboot -> done
            phase:
                typeof window !== "undefined" &&
                window.sessionStorage.getItem(SEEN_KEY)
                    ? "done"
                    : "idle",
            postShown: 0, // how many POST lines have streamed in
        };

        this.reduced = prefersReducedMotion();
        this._timers = [];

        // Lazily-created WebAudio context for the ominous crash tone (built only
        // when the crash actually fires, and only if sound is enabled), so we
        // never spin up audio on a muted or untouched page. The same context is
        // reused for the reboot boot-sound plethora.
        this._audioCtx = null;
        // Live oscillators/sources spun up for the reboot sequence, tracked so
        // they can all be stopped on teardown even if the crash is dismissed
        // mid-boot.
        this._rebootNodes = [];

        // The realistic panic eye: a ref to the iris+pupil SVG group so a shared
        // pointer handler can ease its gaze toward the cursor, plus a saccade
        // timer that darts the gaze when the pointer is idle so it always feels
        // alive. Built only while the panic screen is mounted; torn down after.
        this.irisRef = React.createRef();
        this._eyeSaccade = null;
        this._eyeLastMove = 0;

        this.onScroll = rafThrottle(this.checkScroll.bind(this));
        this.handleContinue = this.handleContinue.bind(this);
        this.replay = this.replay.bind(this);
        this.onEyeMove = rafThrottle(this.trackEye.bind(this));
    }

    // ---- Realistic panic eye gaze ------------------------------------------
    // Ease the iris+pupil toward the cursor (clamped inside the sclera so the
    // pupil never slides off). A shared document mousemove drives it; when the
    // pointer goes idle a saccade timer nudges the gaze so the eye keeps moving.
    setEyeGaze(ux, uy) {
        const g = this.irisRef.current;
        if (!g) return;
        g.style.transform = `translate(${ux.toFixed(2)}px, ${uy.toFixed(2)}px)`;
    }

    trackEye(e) {
        const g = this.irisRef.current;
        if (!g) return;
        this._eyeLastMove = Date.now();
        const r = g.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const dx = (e.clientX - cx) / 140;
        const dy = (e.clientY - cy) / 140;
        const len = Math.hypot(dx, dy) || 1;
        const m = Math.min(len, 1) / len;
        // SVG user-unit travel limits (viewBox 0 0 100 80): keep pupil on sclera.
        const ux = Math.max(-1, Math.min(1, dx * m)) * 9;
        const uy = Math.max(-1, Math.min(1, dy * m)) * 5.5;
        this.setEyeGaze(ux, uy);
    }

    startEyeTracking() {
        if (this.reduced) return; // hold a steady, watchful gaze
        window.addEventListener("mousemove", this.onEyeMove, { passive: true });
        // Idle saccades: if the pointer hasn't moved recently, dart the gaze to
        // a small random offset so the watcher never freezes.
        this._eyeSaccade = setInterval(() => {
            if (Date.now() - this._eyeLastMove < 1400) return;
            const ux = (Math.random() * 2 - 1) * 7;
            const uy = (Math.random() * 2 - 1) * 4;
            this.setEyeGaze(ux, uy);
        }, 1600);
    }

    stopEyeTracking() {
        window.removeEventListener("mousemove", this.onEyeMove);
        if (this.onEyeMove.cancel) this.onEyeMove.cancel();
        if (this._eyeSaccade) {
            clearInterval(this._eyeSaccade);
            this._eyeSaccade = null;
        }
    }

    componentDidMount() {
        if (this.state.phase === "idle") {
            window.addEventListener("scroll", this.onScroll, { passive: true });
            // In case the page loads already scrolled (refresh mid-page).
            this.checkScroll();
        }
        // Expose an on-demand replay handle to App so the crash stays a
        // findable feature even after the one-shot has fired this session.
        if (this.props.replayRef) {
            this.props.replayRef.current = this.replay;
        }
    }

    // Start/stop the panic-eye gaze tracking as the panic screen mounts and
    // leaves, so the listener + saccade timer live only while the eye is shown.
    componentDidUpdate(prevProps, prevState) {
        if (this.state.phase === "panic" && prevState.phase !== "panic") {
            this.startEyeTracking();
        } else if (this.state.phase !== "panic" && prevState.phase === "panic") {
            this.stopEyeTracking();
        }
    }

    // Deliberately RE-TRIGGER the crash sequence, ignoring the one-shot
    // sessionStorage gate. Used by the manual "descend" entry so the crash can
    // always be re-discovered. No-op if a crash is already mid-flight.
    replay() {
        const { phase } = this.state;
        if (phase !== "idle" && phase !== "done") return;
        window.removeEventListener("scroll", this.onScroll);
        // Reset to idle first so crash()'s guard passes, then crash.
        this.setState({ phase: "idle", postShown: 0 }, () => this.crash());
    }

    componentWillUnmount() {
        window.removeEventListener("scroll", this.onScroll);
        if (this.onScroll.cancel) this.onScroll.cancel();
        window.removeEventListener("keydown", this.handleContinue);
        this.stopEyeTracking();
        this._timers.forEach((t) => clearTimeout(t));
        this.teardownAudio();
        if (this.props.replayRef && this.props.replayRef.current === this.replay) {
            this.props.replayRef.current = null;
        }
    }

    later(fn, ms) {
        const t = setTimeout(fn, ms);
        this._timers.push(t);
        return t;
    }

    // ---- Ominous computerized crash tone (synthesized, no audio file) -------
    // Fired the instant the panic hits. Two voices layered:
    //   1) a detuning DESCENDING sub-bass drop (the floor giving way), and
    //   2) a dissonant BIT-CRUSHED digital cluster/sweep (the machine breaking)
    //      built from a few clashing sawtooth partials pushed through a
    //      waveshaper for a crunchy, quantized digital edge.
    // Fully gated on the global sound toggle: skipped entirely when muted.
    playCrashTone() {
        if (this.props.soundEnabled === false) return;
        if (typeof window === "undefined") return;
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;

        let ctx = this._audioCtx;
        try {
            if (!ctx) {
                ctx = new AC();
                this._audioCtx = ctx;
            }
            if (ctx.state === "suspended" && ctx.resume) ctx.resume();
        } catch (e) {
            return; // audio unavailable — fail silently
        }

        const now = ctx.currentTime;
        const master = ctx.createGain();
        master.gain.setValueAtTime(0.0001, now);
        master.gain.exponentialRampToValueAtTime(0.5, now + 0.04);
        master.gain.exponentialRampToValueAtTime(0.0008, now + 2.4);
        master.connect(ctx.destination);

        // --- Voice 1: detuning descending sub-bass drop -----------------------
        const sub = ctx.createOscillator();
        sub.type = "sawtooth";
        sub.frequency.setValueAtTime(140, now);
        sub.frequency.exponentialRampToValueAtTime(28, now + 1.9);
        const subDetune = ctx.createOscillator();
        subDetune.type = "sine";
        subDetune.frequency.setValueAtTime(146, now); // beats against sub
        subDetune.frequency.exponentialRampToValueAtTime(30, now + 1.9);
        const subGain = ctx.createGain();
        subGain.gain.setValueAtTime(0.9, now);
        sub.connect(subGain);
        subDetune.connect(subGain);
        subGain.connect(master);

        // --- Voice 2: dissonant bit-crushed digital cluster/sweep -------------
        // A waveshaper with a hard, stepped curve gives a quantized "bit-crush"
        // crunch; a lowpass sweeps down so the cluster collapses with the drop.
        const shaper = ctx.createWaveShaper();
        const curveLen = 256;
        const curve = new Float32Array(curveLen);
        const steps = 6; // few quantization steps = crushed digital edge
        for (let i = 0; i < curveLen; i++) {
            const x = (i / (curveLen - 1)) * 2 - 1;
            curve[i] = Math.round(x * steps) / steps;
        }
        shaper.curve = curve;
        shaper.oversample = "none";

        const lp = ctx.createBiquadFilter();
        lp.type = "lowpass";
        lp.frequency.setValueAtTime(3200, now);
        lp.frequency.exponentialRampToValueAtTime(320, now + 1.6);
        lp.Q.setValueAtTime(7, now);

        const clusterGain = ctx.createGain();
        clusterGain.gain.setValueAtTime(0.18, now);
        clusterGain.gain.exponentialRampToValueAtTime(0.0008, now + 2.1);

        // clashing partials (a dissonant, slightly out-of-tune cluster)
        const partials = [311.1, 370.0, 392.0, 523.25, 554.37];
        const clusterOscs = partials.map((f, i) => {
            const o = ctx.createOscillator();
            o.type = "sawtooth";
            o.frequency.setValueAtTime(f, now);
            // sweep the whole cluster downward so it detunes as it collapses
            o.frequency.exponentialRampToValueAtTime(f * 0.4, now + 1.8);
            o.detune.setValueAtTime((i - 2) * 14, now);
            o.connect(shaper);
            return o;
        });
        shaper.connect(lp);
        lp.connect(clusterGain);
        clusterGain.connect(master);

        const all = [sub, subDetune, ...clusterOscs];
        all.forEach((o) => {
            try { o.start(now); } catch (e) { /* already started */ }
            try { o.stop(now + 2.5); } catch (e) { /* no-op */ }
        });
    }

    // ---- Reboot / POST boot-sound plethora (synthesized, no audio file) -----
    // Fired when the panic is dismissed and the machine reboots into the Annex.
    // A dense, layered 80s/90s reboot built entirely from WebAudio primitives,
    // timed to the POST lines streaming in:
    //   - a power-up hum + fan whir that rises and settles,
    //   - a sharp POST beep as each diagnostic line lands,
    //   - a sweeping memory-test tone,
    //   - disk-seek chatter (a flurry of filtered noise clicks),
    //   - scattered hardware-init blips,
    //   - a two-note BIOS chime,
    //   - and a final bright "annex online" confirmation tone.
    // Fully gated on the global sound toggle: skipped entirely when muted. The
    // `step` (ms between POST lines) and `lineCount` are threaded in so the
    // beeps land with the text. All created nodes are tracked for teardown.
    playRebootSounds(step, lineCount) {
        if (this.props.soundEnabled === false) return;
        if (typeof window === "undefined") return;
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;

        let ctx = this._audioCtx;
        try {
            if (!ctx) {
                ctx = new AC();
                this._audioCtx = ctx;
            }
            if (ctx.state === "suspended" && ctx.resume) ctx.resume();
        } catch (e) {
            return; // audio unavailable — fail silently
        }

        const now = ctx.currentTime;
        const sec = step / 1000; // POST line cadence, in seconds
        const total = sec * (lineCount + 1);
        const track = (n) => { this._rebootNodes.push(n); return n; };

        // Master bus for the whole reboot, kept modest so the layers stack
        // without clipping.
        const master = ctx.createGain();
        master.gain.setValueAtTime(0.0001, now);
        master.gain.exponentialRampToValueAtTime(0.42, now + 0.12);
        master.connect(ctx.destination);

        // --- Power-up hum + fan whir: a low rising drone that settles in -------
        const hum = track(ctx.createOscillator());
        hum.type = "sawtooth";
        hum.frequency.setValueAtTime(42, now);
        hum.frequency.exponentialRampToValueAtTime(118, now + 0.5);
        hum.frequency.exponentialRampToValueAtTime(96, now + 1.4);
        const humLp = ctx.createBiquadFilter();
        humLp.type = "lowpass";
        humLp.frequency.setValueAtTime(220, now);
        humLp.frequency.exponentialRampToValueAtTime(620, now + 0.6);
        const humGain = ctx.createGain();
        humGain.gain.setValueAtTime(0.0001, now);
        humGain.gain.exponentialRampToValueAtTime(0.16, now + 0.4);
        humGain.gain.setValueAtTime(0.16, now + total - 0.4);
        humGain.gain.exponentialRampToValueAtTime(0.0008, now + total + 0.3);
        hum.connect(humLp); humLp.connect(humGain); humGain.connect(master);
        try { hum.start(now); hum.stop(now + total + 0.4); } catch (e) { /* no-op */ }

        // Fan-whir layer: a shaped noise loop wobbling under the hum.
        const fanBuf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.5), ctx.sampleRate);
        const fd = fanBuf.getChannelData(0);
        for (let i = 0; i < fd.length; i++) fd[i] = (Math.random() * 2 - 1) * 0.5;
        const fan = track(ctx.createBufferSource());
        fan.buffer = fanBuf; fan.loop = true;
        const fanBp = ctx.createBiquadFilter();
        fanBp.type = "bandpass";
        fanBp.frequency.setValueAtTime(280, now);
        fanBp.Q.setValueAtTime(1.6, now);
        const fanGain = ctx.createGain();
        fanGain.gain.setValueAtTime(0.0001, now);
        fanGain.gain.exponentialRampToValueAtTime(0.05, now + 0.6);
        fanGain.gain.setValueAtTime(0.05, now + total - 0.4);
        fanGain.gain.exponentialRampToValueAtTime(0.0006, now + total + 0.2);
        fan.connect(fanBp); fanBp.connect(fanGain); fanGain.connect(master);
        try { fan.start(now); fan.stop(now + total + 0.3); } catch (e) { /* no-op */ }

        // --- A short, sharp digital beep helper (a POST tick) -----------------
        const beep = (t, freq, dur, vol, type) => {
            const o = track(ctx.createOscillator());
            o.type = type || "square";
            o.frequency.setValueAtTime(freq, t);
            const g = ctx.createGain();
            g.gain.setValueAtTime(0.0001, t);
            g.gain.exponentialRampToValueAtTime(vol, t + 0.006);
            g.gain.exponentialRampToValueAtTime(0.0005, t + dur);
            o.connect(g); g.connect(master);
            try { o.start(t); o.stop(t + dur + 0.02); } catch (e) { /* no-op */ }
        };

        // A flurry of disk-seek clicks: short bursts of filtered noise.
        const seek = (t, count, spread) => {
            for (let i = 0; i < count; i++) {
                const ct = t + Math.random() * spread;
                const cb = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.02), ctx.sampleRate);
                const cd = cb.getChannelData(0);
                for (let j = 0; j < cd.length; j++) {
                    cd[j] = (Math.random() * 2 - 1) * (1 - j / cd.length);
                }
                const src = track(ctx.createBufferSource());
                src.buffer = cb;
                const bp = ctx.createBiquadFilter();
                bp.type = "bandpass";
                bp.frequency.setValueAtTime(1800 + Math.random() * 2600, ct);
                bp.Q.setValueAtTime(6, ct);
                const g = ctx.createGain();
                g.gain.setValueAtTime(0.16, ct);
                g.gain.exponentialRampToValueAtTime(0.0005, ct + 0.04);
                src.connect(bp); bp.connect(g); g.connect(master);
                try { src.start(ct); src.stop(ct + 0.05); } catch (e) { /* no-op */ }
            }
        };

        // --- POST beeps + init blips, one per streaming diagnostic line -------
        // Each line lands at now + step*(i+1); land a tick just before it so the
        // beep and the text read as one event.
        for (let i = 0; i < lineCount; i++) {
            const t = now + sec * (i + 1) - 0.04;
            // alternate a low/high diagnostic beep; scatter tiny init blips
            beep(t, i % 2 === 0 ? 660 : 990, 0.07, 0.12, "square");
            beep(t + 0.03, 1480 + (i % 3) * 220, 0.03, 0.05, "square");
            seek(t + 0.04, 3 + (i % 3), sec * 0.5); // disk chatter under each line
        }

        // --- Memory-test sweep: a tone climbing across the first third --------
        const mem = track(ctx.createOscillator());
        mem.type = "triangle";
        mem.frequency.setValueAtTime(300, now + sec * 0.6);
        mem.frequency.exponentialRampToValueAtTime(1600, now + sec * 2.4);
        const memGain = ctx.createGain();
        memGain.gain.setValueAtTime(0.0001, now + sec * 0.6);
        memGain.gain.exponentialRampToValueAtTime(0.06, now + sec * 0.9);
        memGain.gain.exponentialRampToValueAtTime(0.0006, now + sec * 2.5);
        mem.connect(memGain); memGain.connect(master);
        try { mem.start(now + sec * 0.6); mem.stop(now + sec * 2.6); } catch (e) { /* no-op */ }

        // --- Two-note BIOS chime as the boot nears completion -----------------
        const chimeAt = now + sec * Math.max(1, lineCount - 1.5);
        beep(chimeAt, 784, 0.16, 0.1, "triangle");        // G5
        beep(chimeAt + 0.13, 1047, 0.2, 0.1, "triangle"); // C6

        // --- "Annex online" confirmation tone: a bright rising major triad ----
        const confAt = now + total - sec * 0.3;
        [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => {
            const o = track(ctx.createOscillator());
            o.type = "triangle";
            o.frequency.setValueAtTime(f, confAt + i * 0.05);
            const g = ctx.createGain();
            g.gain.setValueAtTime(0.0001, confAt + i * 0.05);
            g.gain.exponentialRampToValueAtTime(0.11, confAt + i * 0.05 + 0.04);
            g.gain.exponentialRampToValueAtTime(0.0006, confAt + 0.7);
            o.connect(g); g.connect(master);
            try { o.start(confAt + i * 0.05); o.stop(confAt + 0.8); } catch (e) { /* no-op */ }
        });
    }

    // Stop + drop any live reboot oscillators/sources (called on teardown so a
    // crash dismissed mid-boot doesn't leave nodes ringing).
    stopRebootSounds() {
        this._rebootNodes.forEach((n) => {
            try { n.stop(); } catch (e) { /* already stopped */ }
            try { n.disconnect(); } catch (e) { /* no-op */ }
        });
        this._rebootNodes = [];
    }

    // Close + drop the WebAudio context when the crash leaves the stage.
    teardownAudio() {
        this.stopRebootSounds();
        const ctx = this._audioCtx;
        this._audioCtx = null;
        if (ctx && ctx.state !== "closed" && ctx.close) {
            try { ctx.close(); } catch (e) { /* no-op */ }
        }
    }

    // Fire the crash once the visitor is well into scrolling the board — "at some
    // point while scrolling it breaks". Trips at ~45% scroll depth, or near the
    // very bottom (covers short pages too).
    checkScroll() {
        if (this.state.phase !== "idle") return;
        const doc = document.documentElement;
        const viewBottom = window.scrollY + window.innerHeight;
        const full = Math.max(doc.scrollHeight, document.body.scrollHeight);
        const scrollable = full - window.innerHeight;
        const progress = scrollable > 0 ? window.scrollY / scrollable : 1;
        if (progress >= 0.45 || viewBottom >= full - 160) this.crash();
    }

    crash() {
        window.removeEventListener("scroll", this.onScroll);
        try {
            window.sessionStorage.setItem(SEEN_KEY, "1");
        } catch (e) {
            /* private mode — fine, just won't persist */
        }

        if (this.reduced) {
            // Reduced motion: skip the glitch, go straight to a calm panic card.
            this.playCrashTone();
            this.setState({ phase: "panic" });
            window.addEventListener("keydown", this.handleContinue);
            return;
        }

        // The ominous computerized tone-shift hits with the glitch, riding the
        // panic in (the descending sub-bass + bit-crushed cluster).
        this.playCrashTone();
        this.setState({ phase: "glitch" });
        this.later(() => {
            this.setState({ phase: "panic" });
            window.addEventListener("keydown", this.handleContinue);
        }, 900);
    }

    // "Press any key to continue" — key, click or touch all dismiss the panic.
    handleContinue() {
        if (this.state.phase !== "panic") return;
        window.removeEventListener("keydown", this.handleContinue);
        this.setState({ phase: "reboot", postShown: 0 });

        const step = this.reduced ? 120 : 360;
        // The layered boot-sound plethora, timed to the POST lines streaming in
        // (gated on soundEnabled inside the method).
        this.playRebootSounds(step, POST_LINES.length);
        POST_LINES.forEach((_, i) => {
            this.later(() => this.setState({ postShown: i + 1 }), step * (i + 1));
        });

        // After the POST finishes, unlock THE ANNEX and dismiss the crash. The
        // closing thank-you is no longer a full-screen takeover — App rests a
        // SUBTLE note at the bottom of the page (revealed via onUnlock) instead.
        const total = step * (POST_LINES.length + 1) + (this.reduced ? 150 : 650);
        this.later(() => this.finishCrash(), total);
    }

    // Hand off to App (unlock the Annex + reveal the subtle bottom thank-you)
    // and quietly retire the crash overlay. Safe to call once per run.
    finishCrash() {
        if (this.state.phase !== "reboot") return;
        if (typeof this.props.onUnlock === "function") this.props.onUnlock();
        this.teardownAudio();
        this.setState({ phase: "done" });
    }

    // The ever-watching eye, rendered as an anatomically convincing inline SVG
    // (same lineage as the site's other realistic eyes), tinted into the CRT /
    // kernel-panic mood: a cool steel-blue iris built from a radial gradient
    // plus fine radial fiber striations, a darker limbal ring + lighter inner
    // collarette, a black pupil with a soft depth halo, a glossy specular
    // catchlight, a wet veined sclera that is NOT flat white, an almond lid
    // outline with a crease shadow and lashes, and an upper lid that sweeps
    // open as the panic settles. The iris+pupil group (.sc-eye-iris) eases
    // toward the cursor; the pupil (.sc-eye-pupil) is the black disc. Scanlines
    // and a faint phosphor glow keep it inside the panic's CRT atmosphere.
    renderEye() {
        // Fine radial fiber striations, alternating light/dark for a fibrous
        // iris read. Precomputed each render (cheap; ~28 short lines).
        const fibers = [];
        const N = 28;
        for (let i = 0; i < N; i++) {
            const a = (i / N) * Math.PI * 2 + (i % 3) * 0.05;
            const ri = 4.4; // just outside the pupil
            const ro = 9.4; // out to the iris rim
            fibers.push({
                x1: (Math.cos(a) * ri).toFixed(2),
                y1: (Math.sin(a) * ri).toFixed(2),
                x2: (Math.cos(a) * ro).toFixed(2),
                y2: (Math.sin(a) * ro).toFixed(2),
                // cool CRT steel-blue fibers; alternating brightness
                color: i % 2 === 0 ? "#9fc4ff" : "#274a86",
                w: 0.18 + (i % 3) * 0.05,
            });
        }

        return (
            <div className="sc-eye sc-eye--photo" aria-hidden="true">
                {/* Photoreal generated eyeball — the panic's staring eye. The old
                    vector eye is kept but hidden (sc-eye-svg--off) as a fallback. */}
                <img className="sc-eye-photo" src={crashEye} alt="" />
                {/* Soft dark pupil overlay registered over the photo's real
                    pupil, dilating in and out (CSS) so the eye reads as
                    breathing. Radial-gradient black blends it into the photo;
                    held steady under prefers-reduced-motion. */}
                <div className="sc-eye-pupil-overlay" aria-hidden="true" />
                <div className="sc-eye-veil" aria-hidden="true" />
                <svg
                    className="sc-eye-svg sc-eye-svg--off"
                    viewBox="0 0 100 80"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <defs>
                        {/* wet sclera: cool, softly shaded, brighter toward the
                            top-left light — never a flat white */}
                        <radialGradient id="sc-eye-sclera" cx="42%" cy="34%" r="74%">
                            <stop offset="0%" stopColor="#e9efff" />
                            <stop offset="58%" stopColor="#b9c4e6" />
                            <stop offset="100%" stopColor="#5a6aa0" />
                        </radialGradient>

                        {/* iris: lighter inner ring -> dark steel-blue rim */}
                        <radialGradient id="sc-eye-iris-grad" cx="50%" cy="46%" r="56%">
                            <stop offset="0%" stopColor="#8fb6ff" />
                            <stop offset="48%" stopColor="#3f63bc" />
                            <stop offset="100%" stopColor="#0b1d52" />
                        </radialGradient>

                        {/* almond opening — clips the eyeball contents to the lids */}
                        <clipPath id="sc-eye-lidclip">
                            <path d="M 12 40 C 27 20, 73 20, 88 40 C 73 60, 27 60, 12 40 Z" />
                        </clipPath>
                    </defs>

                    {/* ---- eyeball contents, clipped to the almond ---- */}
                    <g clipPath="url(#sc-eye-lidclip)">
                        {/* shaded wet sclera */}
                        <rect x="0" y="0" width="100" height="80" fill="url(#sc-eye-sclera)" />

                        {/* upper curvature shadow + warm caruncle hint */}
                        <ellipse cx="50" cy="24" rx="42" ry="15" fill="#0a1430" opacity="0.22" />
                        <ellipse cx="15" cy="42" rx="5" ry="3.4" fill="#b97f8f" opacity="0.4" />

                        {/* faint branching red veins crawling in from the corners */}
                        <g stroke="#c83c46" strokeWidth="0.5" fill="none" opacity="0.5" strokeLinecap="round">
                            <path d="M 14 38 C 21 36, 26 39, 31 37 M 28 38 C 31 40, 34 39, 37 41" />
                            <path d="M 86 44 C 79 45, 75 42, 70 44 M 80 45 C 82 47, 85 46, 87 48" />
                            <path d="M 50 60 C 48 56, 52 54, 50 50" />
                        </g>

                        {/* ---- iris + pupil group : eased toward the cursor ---- */}
                        <g className="sc-eye-iris" ref={this.irisRef}>
                            <g transform="translate(50 40)">
                                {/* iris base gradient disc */}
                                <circle r="9.6" fill="url(#sc-eye-iris-grad)" />

                                {/* fine radial fiber striations */}
                                <g strokeLinecap="round">
                                    {fibers.map((f, i) => (
                                        <line
                                            key={i}
                                            x1={f.x1} y1={f.y1}
                                            x2={f.x2} y2={f.y2}
                                            stroke={f.color}
                                            strokeWidth={f.w}
                                            opacity="0.6"
                                        />
                                    ))}
                                </g>

                                {/* lighter collarette ring just outside the pupil */}
                                <circle r="5" fill="none" stroke="#bcd4ff" strokeWidth="0.5" opacity="0.6" />
                                {/* darker limbal ring at the iris edge */}
                                <circle r="9.5" fill="none" stroke="#070b16" strokeWidth="1" opacity="0.9" />

                                {/* pupil — black with a soft depth halo */}
                                <circle r="4.7" fill="#000" opacity="0.4" />
                                <circle className="sc-eye-pupil" r="3.9" fill="#000" />

                                {/* glossy specular catchlight (top-left) + faint secondary */}
                                <circle cx="-2.7" cy="-3.1" r="1.7" fill="#ffffff" opacity="0.95" />
                                <circle cx="2.1" cy="2.5" r="0.8" fill="#ffffff" opacity="0.5" />
                            </g>
                        </g>

                        {/* wet glare sweeping across the top of the eyeball */}
                        <path d="M 18 32 C 34 26, 66 26, 82 32 C 66 30, 34 30, 18 32 Z"
                              fill="#ffffff" opacity="0.2" />
                    </g>

                    {/* ---- upper-lid : skin-dark, sweeps OPEN as panic settles ---- */}
                    <g clipPath="url(#sc-eye-lidclip)">
                        <rect className="sc-eye-lid" x="8" y="18" width="84" height="24" fill="#05060f" />
                    </g>

                    {/* ---- almond rim ink outline (upper + lower lid arcs) ---- */}
                    <path
                        d="M 12 40 C 27 20, 73 20, 88 40 C 73 60, 27 60, 12 40 Z"
                        fill="none" stroke="#03060e" strokeWidth="1.4"
                    />
                    {/* soft crease shadow above the upper lid */}
                    <path
                        d="M 16 33 C 31 20, 69 20, 84 33"
                        fill="none" stroke="#0a1430" strokeWidth="2" opacity="0.45"
                    />

                    {/* a few upper lashes, curling up and out */}
                    <g stroke="#03060e" strokeWidth="1" fill="none" strokeLinecap="round">
                        <path d="M 29 25 q -2 -4, -4 -6" />
                        <path d="M 42 22 q -1 -4, -2 -7" />
                        <path d="M 58 22 q 1 -4, 2 -7" />
                        <path d="M 71 25 q 2 -4, 4 -6" />
                    </g>
                    {/* one lower lash hint */}
                    <path d="M 50 58 l 0 5" stroke="#03060e" strokeWidth="0.7" />
                </svg>
            </div>
        );
    }

    render() {
        const { phase, postShown } = this.state;
        if (phase === "idle" || phase === "done") return null;

        if (phase === "glitch") {
            return (
                <div className="sc-glitch" aria-hidden="true">
                    <div className="sc-glitch-layer sc-glitch-r" />
                    <div className="sc-glitch-layer sc-glitch-g" />
                    <div className="sc-glitch-layer sc-glitch-b" />
                    <div className="sc-glitch-scan" />
                </div>
            );
        }

        if (phase === "reboot") {
            return (
                <div className="sc-screen sc-reboot" role="alertdialog" aria-label="Rebooting">
                    <div className="sc-scanlines" aria-hidden="true" />
                    <pre className="sc-post">
                        {POST_LINES.slice(0, postShown).map((line, i) => (
                            <div
                                key={i}
                                className={
                                    "sc-post-line" +
                                    (i === POST_LINES.length - 1 ? " sc-post-final" : "")
                                }
                            >
                                {line}
                            </div>
                        ))}
                        <span className="sc-cursor sc-cursor-block">&#9608;</span>
                    </pre>
                </div>
            );
        }

        // phase === "panic"
        return (
            <div
                className="sc-screen sc-panic"
                role="alertdialog"
                aria-label="A fatal exception has occurred"
                onClick={this.handleContinue}
                onTouchEnd={this.handleContinue}
            >
                <div className="sc-scanlines" aria-hidden="true" />

                <div className="sc-panic-inner">
                    <div className="sc-banner">
                        <span className="sc-banner-glitch" data-text="AMBULATE/OS">
                            AMBULATE/OS
                        </span>
                        <span className="sc-banner-tag"> kernel panic</span>
                    </div>

                    <p className="sc-line sc-jit-1">
                        PANIC: fatal exception <span className="sc-hi">0E</span> at
                        <span className="sc-hi"> 0xAMBU:C0DE</span> in module
                        <span className="sc-hi"> THE_EYE.VXD</span>
                    </p>
                    <p className="sc-line sc-jit-2">
                        *** the current process will be terminated ***
                    </p>

                    <ul className="sc-list">
                        <li className="sc-jit-3">[ FAIL ] AMBULATE.EXE has stopped responding</li>
                        <li className="sc-jit-1">[ WARN ] something beneath the floor has begun to ambulate</li>
                        <li className="sc-jit-2">[ ???? ] an unindexed wing has been detected</li>
                    </ul>

                    {this.renderEye()}

                    <p className="sc-continue sc-jit-3">
                        press any key to continue<span className="sc-cursor sc-cursor-block">&#9608;</span>
                    </p>
                </div>
            </div>
        );
    }
}

export default SystemCrash;
